/**
 * Tier Progress — Level progression computation for UI display
 *
 * Computes tier-based progress information for the student-facing
 * progress visualization.
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 */

import {
  type ModuleId,
  type TierConfig,
  getTierConfig,
  getTiersForGroup,
  TIERS,
  getQuestionsForTier,
} from '../content'
import { GroupId } from '../config/systemConfig'
import { QuestionState } from './types'
import { V11_CONFIG } from './selection/selectionConfig'
import { getQuestionStateKey } from './questionAttemptHistory'
import {
  projectMasteryToUnlock,
  type UnlockState,
} from '../shared/proxyContracts/masteryProjection'

/**
 * FG2 — Per-tier mastery facts injected by the caller (dependency inversion).
 * Returning `null` means "no mastery data for this tier" → count-path only.
 * The signature deliberately does NOT import familyMasteryEngine; the caller
 * computes / looks up the facts and hands them in.
 */
export interface TierMasteryFacts {
  /** Family ELO / mastery score for this tier's family (0..100). */
  masteryScore: number
  /** Distinct questions seen in this tier. */
  uniqueQuestionsSeen: number
  /** True when ≥1 question from the hardest difficulty band touched. */
  touchedHighestDifficultyBand: boolean
  /**
   * FG2 Phase 2 — Total number of distinct equivalence classes among the
   * questions in this tier. Optional: when supplied together with
   * `uniqueEquivalenceClassesSeen` (and > 0) the proxy contract switches
   * its breadth denominator from raw question count to class count. Omit
   * for Phase-1 behaviour.
   */
  totalEquivalenceClasses?: number
  /**
   * FG2 Phase 2 — Number of distinct equivalence classes for which the
   * learner has answered at least one representative in this tier.
   */
  uniqueEquivalenceClassesSeen?: number
}

export type TierMasteryProvider = (tier: number) => TierMasteryFacts | null

export interface TierProgressInfo {
  tiers: TierConfig[]
  currentTier: number
  tierDetails: {
    tier: number
    label: string
    title: string
    isUnlocked: boolean
    isCurrent: boolean
    isCompleted: boolean
    totalQuestions: number
    attemptedCount: number
    practicedOrMasteredCount: number
    progressPercent: number
    group: number
    /**
     * FG2 — Which unlock path granted completion (undefined when no mastery
     * provider was supplied — backwards-compatible legacy callers).
     */
    via?: UnlockState['via']
    /** FG2 — Coverage (0..1) of unique variants seen (count-path measure). */
    coverage?: number
    /** FG2 — Mastery readiness (0..1) toward the fast-path threshold. */
    masteryProgress?: number
    /**
     * FG2 — Subtle UI nudge: learner has high mastery but coverage is still
     * low. Surface "X varianten nog niet gezien" — NOT a blocker.
     */
    coverageGapWithHighMastery?: boolean
    /**
     * FG2 Phase 2 — Which denominator drove `coverage` (`'questions'` for
     * legacy / Phase-1, `'equivalence-classes'` when the provider supplied
     * valid class counts). Telemetry + UI can honestly say "3/7 tafels"
     * rather than "12/100 vragen" for flat-distribution families.
     */
    breadthBasis?: UnlockState['breadthBasis']
  }[]
  // R6-4 fix (mei 2026): `fastTrackActive` removed. Was @deprecated since
  // v11, always `false`, with zero external consumers (grep-verified).
}

/**
 * Compute tier progress for display.
 *
 * @param familyMasteryProvider OPTIONAL — when supplied, the FG2 mastery
 *   fast-path is evaluated alongside the existing count path (OR-logic).
 *   When omitted the function returns identical values to the pre-FG2
 *   behaviour (count-path only) so callers can migrate incrementally.
 */
export function computeTierProgress(
  moduleId: ModuleId,
  group: GroupId,
  questionStates: Record<string, QuestionState>,
  startingTier: number,
  familyMasteryProvider?: TierMasteryProvider,
): TierProgressInfo {
  const groupTiers = getTiersForGroup(group)
  const effectiveStartingTier = startingTier || groupTiers[0]?.tier || 1

  // Show tiers for current group + small lookahead
  const currentGroupEnd = groupTiers[groupTiers.length - 1]?.tier || 18
  const nextGroupStart =
    group < 8 ? getTiersForGroup((group + 1) as GroupId) : []
  const lookaheadTiers = new Set(nextGroupStart.slice(0, 2).map((t) => t.tier))
  const visibleTiers = TIERS.filter(
    (t) =>
      (t.tier >= effectiveStartingTier && t.tier <= currentGroupEnd) ||
      lookaheadTiers.has(t.tier),
  )

  // Compute unlocked tiers (simplified for v11: based on previous tier completion)
  const unlockedTiers = new Set<number>()
  unlockedTiers.add(effectiveStartingTier)

  // Unlock tiers where the user has already practiced questions
  visibleTiers.forEach((tierConfig) => {
    const questionsAtTier = getQuestionsForTier(moduleId, tierConfig.tier)
    const hasAttempted = questionsAtTier.some((q) => {
      const stateKey = getQuestionStateKey(q.id, group)
      const state = questionStates[stateKey]
      return state && state.attempts > 0
    })
    if (hasAttempted) {
      unlockedTiers.add(tierConfig.tier)
    }
  })

  const tierDetails = visibleTiers.map((tierConfig) => {
    const questionsAtTier = getQuestionsForTier(moduleId, tierConfig.tier)
    let attemptedCount = 0
    let practicedOrMasteredCount = 0
    questionsAtTier.forEach((q) => {
      const stateKey = getQuestionStateKey(q.id, group)
      const state = questionStates[stateKey]
      if (state && state.attempts > 0) {
        attemptedCount++
        if (state.status === 'practiced' || state.status === 'mastered') {
          practicedOrMasteredCount++
        }
      }
    })
    const totalQuestions = questionsAtTier.length

    // ── FG2: OR-logic completion when a mastery provider is supplied ────
    // Legacy path (no provider): count-only completion identical to pre-FG2.
    const facts = familyMasteryProvider?.(tierConfig.tier) ?? null
    const meetsMinAttempted =
      attemptedCount >= V11_CONFIG.LEVEL_UNLOCK_MIN_ATTEMPTED

    let projection: UnlockState | null = null
    if (facts && totalQuestions > 0) {
      projection = projectMasteryToUnlock({
        practicedOrMasteredCount,
        totalQuestions,
        levelUnlockThreshold: V11_CONFIG.LEVEL_UNLOCK_THRESHOLD,
        familyMasteryScore: facts.masteryScore,
        masteryFastPath: V11_CONFIG.MASTERY_FAST_PATH,
        uniqueQuestionsSeen: facts.uniqueQuestionsSeen,
        breadthGuardFraction: V11_CONFIG.MASTERY_FAST_PATH_BREADTH,
        touchedHighestDifficultyBand:
          V11_CONFIG.MASTERY_FAST_PATH_REQUIRE_HIGH_DIFFICULTY
            ? facts.touchedHighestDifficultyBand
            : true,
        totalEquivalenceClasses: facts.totalEquivalenceClasses,
        uniqueEquivalenceClassesSeen: facts.uniqueEquivalenceClassesSeen,
      })
    }

    const isCompleted =
      totalQuestions > 0 &&
      meetsMinAttempted &&
      (projection
        ? projection.unlocked
        : practicedOrMasteredCount >=
          Math.ceil(totalQuestions * V11_CONFIG.LEVEL_UNLOCK_THRESHOLD))

    // Unlock next tier if this one is completed
    if (isCompleted) {
      unlockedTiers.add(tierConfig.tier + 1)
    }

    // Also unlock if we have significant progress
    if (attemptedCount > 0) {
      unlockedTiers.add(tierConfig.tier)
    }
    const progressPercent =
      totalQuestions > 0
        ? Math.round((practicedOrMasteredCount / totalQuestions) * 100)
        : 0
    return {
      tier: tierConfig.tier,
      label: tierConfig.levelCode,
      title: tierConfig.title,
      isUnlocked: false,
      // Set in second pass
      isCurrent: false,
      // Set later
      isCompleted,
      totalQuestions,
      attemptedCount,
      practicedOrMasteredCount,
      progressPercent,
      group: tierConfig.group,
      ...(projection
        ? {
            via: projection.via,
            coverage: projection.coverage,
            masteryProgress: projection.masteryProgress,
            coverageGapWithHighMastery: projection.coverageGapWithHighMastery,
            breadthBasis: projection.breadthBasis,
          }
        : {}),
    }
  })

  // Second pass to set isUnlocked based on the set we built
  tierDetails.forEach((d) => {
    d.isUnlocked = unlockedTiers.has(d.tier)
  })

  // Determine current tier (highest unlocked and not completed)
  let currentTier = effectiveStartingTier
  for (let i = tierDetails.length - 1; i >= 0; i--) {
    const detail = tierDetails[i]
    if (detail.isUnlocked && !detail.isCompleted && detail.totalQuestions > 0) {
      currentTier = detail.tier
      break
    }
  }

  // If all unlocked are completed, move to next
  const allUnlockedCompleted = tierDetails
    .filter((d) => d.isUnlocked && d.totalQuestions > 0)
    .every((d) => d.isCompleted)
  if (allUnlockedCompleted) {
    let maxUnlocked = 0
    tierDetails.forEach((d) => {
      if (d.isUnlocked) maxUnlocked = Math.max(maxUnlocked, d.tier)
    })
    const next = tierDetails.find((d) => d.tier === maxUnlocked + 1)
    if (next) currentTier = next.tier
    else currentTier = maxUnlocked
  }
  tierDetails.forEach((d) => {
    d.isCurrent = d.tier === currentTier
  })
  return {
    tiers: visibleTiers,
    currentTier,
    tierDetails,
  }
}
